/**
 * DappFormModal Component
 *
 * Modal form for adding or editing minting dapps.
 * Supports both internal and external dapp configurations.
 *
 * @module containers/AdminMintingDapps/components/DappFormModal
 * @category Admin
 */

import React, { useState, useEffect } from 'react';

/**
 * Props for DappFormModal
 */
export interface DappFormModalProps {
  /** Dapp being edited, or null for new dapp */
  dapp: any | null;
  /** Callback when form saved */
  onSave: (dappData: any) => void;
  /** Callback when modal closed */
  onClose: () => void;
}

/**
 * DappFormModal - Modal form for dapp CRUD.
 */
const DappFormModal: React.FC<DappFormModalProps> = ({ dapp, onSave, onClose }) => {
  const isEditing = !!dapp;

  // Form state
  const [formData, setFormData] = useState({
    id: '',
    name: '',
    description: '',
    type: 'internal' as 'internal' | 'external',
    url: '',
    component_code: '', // For internal dapps: JavaScript source code
    config: {
      allowedChains: [19, 14] as number[],
      allowedOrigins: [] as string[],
      sandbox: 'allow-scripts allow-same-origin allow-forms allow-popups',
      requireAuth: true,
    },
    version: '1.0.0',
    author: '',
    icon: '',
    tags: [] as string[],
  });

  const [errors, setErrors] = useState<Record<string, string>>({});

  // Load dapp data into form when editing
  useEffect(() => {
    if (dapp) {
      setFormData({
        id: dapp.id || '',
        name: dapp.name || '',
        description: dapp.description || '',
        type: dapp.type || 'internal',
        url: dapp.url || '',
        component_code: dapp.component_code || '',
        config: {
          allowedChains: dapp.config?.allowedChains || [19, 14],
          allowedOrigins: dapp.config?.allowedOrigins || [],
          sandbox: dapp.config?.sandbox || 'allow-scripts allow-same-origin allow-forms allow-popups',
          requireAuth: dapp.config?.requireAuth ?? true,
        },
        version: dapp.version || '1.0.0',
        author: dapp.author || '',
        icon: dapp.icon || '',
        tags: dapp.tags || [],
      });
    } else {
      // Reset for new dapp
      setFormData({
        id: '',
        name: '',
        description: '',
        type: 'internal',
        url: '',
        component_code: '',
        config: {
          allowedChains: [19, 14],
          allowedOrigins: [],
          sandbox: 'allow-scripts allow-same-origin allow-forms allow-popups',
          requireAuth: true,
        },
        version: '1.0.0',
        author: '',
        icon: '',
        tags: [],
      });
    }
    setErrors({});
  }, [dapp]);

  // Handle field changes
  const handleChange = (field: string, value: any) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    // Clear error when user starts typing
    if (errors[field]) {
      setErrors(prev => ({ ...prev, [field]: '' }));
    }
  };

  const handleConfigChange = (field: string, value: any) => {
    setFormData(prev => ({
      ...prev,
      config: { ...prev.config, [field]: value }
    }));
  };

  // Handle chain selection toggles
  const toggleChain = (chainId: number) => {
    const chains = [...formData.config.allowedChains];
    const index = chains.indexOf(chainId);
    if (index > -1) {
      chains.splice(index, 1);
    } else {
      chains.push(chainId);
    }
    handleConfigChange('allowedChains', chains);
  };

  // Handle tags input
  const [tagInput, setTagInput] = useState('');
  const addTag = () => {
    const tag = tagInput.trim().toLowerCase();
    if (tag && !formData.tags.includes(tag)) {
      setFormData(prev => ({
        ...prev,
        tags: [...prev.tags, tag]
      }));
      setTagInput('');
    }
  };
  const removeTag = (tag: string) => {
    setFormData(prev => ({
      ...prev,
      tags: prev.tags.filter(t => t !== tag)
    }));
  };

  // Validate form
  const validate = () => {
    const newErrors: Record<string, string> = {};

    if (!formData.id.trim()) {
      newErrors.id = 'ID is required';
    } else if (!/^[a-z0-9]+(?:-[a-z0-9]+)*$/.test(formData.id)) {
      newErrors.id = 'ID must be lowercase kebab-case (e.g., "my-dapp")';
    }

    if (!formData.name.trim()) {
      newErrors.name = 'Name is required';
    }

    if (formData.type === 'external' && !formData.url.trim()) {
      newErrors.url = 'URL is required for external dapps';
    }

    if (formData.type === 'internal' && !formData.component_code?.trim()) {
      newErrors.component_code = 'Component code is required for internal dapps';
    }

    if (formData.type === 'external' && formData.url) {
      try {
        new URL(formData.url);
      } catch {
        newErrors.url = 'Invalid URL format';
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // Handle submit
  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!validate()) return;

    onSave(formData);
  };

  // Available chains (quick reference)
  const commonChains = [
    { id: 1, name: 'Ethereum' },
    { id: 19, name: 'Songbird' },
    { id: 14, name: 'Flare' },
    { id: 16, name: 'Coston' },
    { id: 114, name: 'Coston2' },
  ];

  return (
    <div className="modal show d-block" tabIndex={-1} style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}>
      <div className="modal-dialog modal-lg modal-dialog-centered">
        <div className="modal-content">
          <div className="modal-header">
            <h5 className="modal-title">
              {isEditing ? (
                <>Edit Dapp: <strong>{dapp.name}</strong></>
              ) : (
                <>Add New Minting Dapp</>
              )}
            </h5>
            <button type="button" className="btn-close" onClick={onClose}></button>
          </div>

          <form onSubmit={handleSubmit}>
            <div className="modal-body">
              {/* Basic Info */}
              <div className="row g-3 mb-4">
                <div className="col-md-6">
                  <label className="form-label">ID <span className="text-danger">*</span></label>
                  <input
                    type="text"
                    className={`form-control ${errors.id ? 'is-invalid' : ''}`}
                    value={formData.id}
                    onChange={(e) => handleChange('id', e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, ''))}
                    placeholder="my-dapp"
                    disabled={isEditing}
                    required
                  />
                  {errors.id && <div className="invalid-feedback">{errors.id}</div>}
                  <small className="text-muted">
                    Used in URL: /mint/{'<strong>id</strong>'}
                    {isEditing && <span className="text-warning ms-2">(cannot be changed)</span>}
                  </small>
                </div>
                <div className="col-md-6">
                  <label className="form-label">Name <span className="text-danger">*</span></label>
                  <input
                    type="text"
                    className={`form-control ${errors.name ? 'is-invalid' : ''}`}
                    value={formData.name}
                    onChange={(e) => handleChange('name', e.target.value)}
                    placeholder="My Awesome Dapp"
                    required
                  />
                  {errors.name && <div className="invalid-feedback">{errors.name}</div>}
                </div>
                <div className="col-12">
                  <label className="form-label">Description</label>
                  <textarea
                    className="form-control"
                    value={formData.description}
                    onChange={(e) => handleChange('description', e.target.value)}
                    placeholder="Brief description of what this dapp does"
                    rows={2}
                  />
                </div>
                <div className="col-md-4">
                  <label className="form-label">Type <span className="text-danger">*</span></label>
                  <select
                    className="form-select"
                    value={formData.type}
                    onChange={(e) => handleChange('type', e.target.value)}
                  >
                    <option value="internal">Internal (React Component)</option>
                    <option value="external">External (iframe)</option>
                  </select>
                  <small className="text-muted">
                    {formData.type === 'internal'
                      ? 'Component must be registered in code'
                      : 'External dapp hosted on separate domain'}
                  </small>
                </div>
                <div className="col-md-4">
                  <label className="form-label">Version</label>
                  <input
                    type="text"
                    className="form-control"
                    value={formData.version}
                    onChange={(e) => handleChange('version', e.target.value)}
                    placeholder="1.0.0"
                  />
                </div>
                <div className="col-md-4">
                  <label className="form-label">Author</label>
                  <input
                    type="text"
                    className="form-control"
                    value={formData.author}
                    onChange={(e) => handleChange('author', e.target.value)}
                    placeholder="Your name or team"
                  />
                </div>
              </div>

              {/* Internal-specific fields */}
              {formData.type === 'internal' && (
                <div className="row g-3 mb-4 form-section">
                  <h6><i className="fas fa-code me-2"></i>Internal Dapp Component</h6>
                  <div className="col-12">
                    <label className="form-label">Component Code (JavaScript) <span className="text-danger">*</span></label>
                    <textarea
                      className={`form-control ${errors.component_code ? 'is-invalid' : ''}`}
                      value={formData.component_code}
                      onChange={(e) => handleChange('component_code', e.target.value)}
                      placeholder="React component code (as JavaScript, ES5+). Must return a React component function."
                      rows={8}
                      required
                    />
                    {errors.component_code && <div className="invalid-feedback">{errors.component_code}</div>}
                    <small className="text-muted">
                      The React component source code. Must evaluate to a React component (function or class). Use global React (provided automatically). Do not use import statements. Available props: {"{ dappId, dappName, appKit, account, chainId, walletState, sendMessage, onMessage }"}.
                    </small>
                  </div>
                </div>
              )}

              {/* External-specific fields */}
              {formData.type === 'external' && (
                <div className="row g-3 mb-4 form-section">
                  <h6><i className="fas fa-globe me-2"></i>External Dapp Configuration</h6>
                  <div className="col-12">
                    <label className="form-label">URL <span className="text-danger">*</span></label>
                    <input
                      type="url"
                      className={`form-control ${errors.url ? 'is-invalid' : ''}`}
                      value={formData.url}
                      onChange={(e) => handleChange('url', e.target.value)}
                      placeholder="https://your-dapp.example.com"
                      required
                    />
                    {errors.url && <div className="invalid-feedback">{errors.url}</div>}
                  </div>
                  <div className="col-12">
                    <label className="form-label">Allowed Origins</label>
                    <div className="input-group">
                      <input
                        type="text"
                        className="form-control"
                        placeholder="https://trusted-origin.com"
                        value=""
                        onChange={(e) => {/* handled dynamically */}}
                      />
                      <button className="btn btn-outline-secondary" type="button" disabled>
                        Add
                      </button>
                    </div>
                    <small className="text-muted">
                      Origins that can embed this dapp (auto-filled from URL)
                    </small>
                    {formData.config.allowedOrigins?.length > 0 && (
                      <div className="mt-2">
                        {formData.config.allowedOrigins.map((origin: string, idx: number) => (
                          <span key={idx} className="badge bg-info me-1">
                            {origin}
                          </span>
                        ))}
                      </div>
                    )}
                  </div>
                </div>
              )}

              {/* Configuration */}
              <div className="form-section">
                <div className="row g-3 mb-4">
                  <div className="col-12">
                    <h6><i className="fas fa-sliders-h me-2"></i>Configuration</h6>
                  </div>
                  <div className="col-md-6">
                    <label className="form-label">Allowed Chains</label>
                    <div className="d-flex flex-wrap gap-2">
                      {commonChains.map(chain => (
                        <button
                          key={chain.id}
                          type="button"
                          className={`btn btn-sm ${formData.config.allowedChains.includes(chain.id) ? 'btn-primary' : 'btn-outline-secondary'}`}
                          onClick={() => toggleChain(chain.id)}
                        >
                          {chain.name} ({chain.id})
                        </button>
                      ))}
                    </div>
                    {formData.config.allowedChains.length === 0 && (
                      <small className="text-warning">No chains selected (will default to all)</small>
                    )}
                  </div>
                  <div className="col-md-6">
                    <label className="form-label">Sandbox Attributes (iframe)</label>
                    <input
                      type="text"
                      className="form-control"
                      value={formData.config.sandbox}
                      onChange={(e) => handleConfigChange('sandbox', e.target.value)}
                      placeholder="allow-scripts allow-same-origin"
                    />
                    <small className="text-muted">
                      Space-separated sandbox flags for iframe
                    </small>
                  </div>
                  <div className="col-12">
                    <div className="form-check form-switch">
                      <input
                        className="form-check-input"
                        type="checkbox"
                        id="requireAuth"
                        checked={formData.config.requireAuth}
                        onChange={(e) => handleConfigChange('requireAuth', e.target.checked)}
                      />
                      <label className="form-check-label" htmlFor="requireAuth">
                        Require wallet connection (recommended)
                      </label>
                    </div>
                  </div>
                </div>
              </div>

              {/* Tags */}
              <div className="row g-3 mb-4">
                <div className="col-12">
                  <h6><i className="fas fa-tags me-2"></i>Tags</h6>
                  <div className="input-group mb-2">
                    <input
                      type="text"
                      className="form-control"
                      placeholder="Add tag (press Enter)"
                      value={tagInput}
                      onChange={(e) => setTagInput(e.target.value)}
                      onKeyPress={(e) => {
                        if (e.key === 'Enter') {
                          e.preventDefault();
                          addTag();
                        }
                      }}
                    />
                    <button
                      type="button"
                      className="btn btn-outline-secondary"
                      onClick={addTag}
                    >
                      Add
                    </button>
                  </div>
                  <div className="d-flex flex-wrap gap-1">
                    {formData.tags.map(tag => (
                      <span key={tag} className="badge bg-primary">
                        {tag}
                        <button
                          type="button"
                          className="btn-close btn-close-white btn-close-sm ms-1"
                          onClick={() => removeTag(tag)}
                        ></button>
                      </span>
                    ))}
                  </div>
                </div>
              </div>

              {/* Icon */}
              <div className="row g-3">
                <div className="col-12">
                  <h6><i className="fas fa-image me-2"></i>Icon URL (optional)</h6>
                  <input
                    type="url"
                    className="form-control"
                    value={formData.icon}
                    onChange={(e) => handleChange('icon', e.target.value)}
                    placeholder="https://example.com/icon.png"
                  />
                  <small className="text-muted">
                    URL to an icon image (recommended size: 64x64px)
                  </small>
                </div>
              </div>
            </div>

            <div className="modal-footer">
              <button type="button" className="btn btn-secondary" onClick={onClose}>
                Cancel
              </button>
              <button type="submit" className="btn btn-primary">
                <i className="fas fa-save me-2"></i>
                {isEditing ? 'Update Dapp' : 'Add Dapp'}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default DappFormModal;
